import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, BoxProps, Modal, Stack, Typography } from "@mui/material";
import { SelectDealFolder } from "components/Prompts";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useModals, usePrompt } from "hooks";
import { isArray } from "lodash";
import { useAccountSelector, useDealActions, useFileScanningSelector } from "store";
import { DealBase, FolderKind, IFile, IFolder } from "store/types";
import pickFiles from "utils/pick-files";

import { BulkActions } from "./BulkActions";
import { FilesSelect } from "./FilesSelect";
import { FilesTable } from "./FilesTable";
import { FolderPathDisplay } from "./FolderPathDisplay";
import { Footer } from "./Footer";
import { Head } from "./Head";
import { Result } from "./Result";
import { StepIndicator } from "./StepIndicator";
import { GLOBAL_WORKSPACE_DEAL, TargetSelect } from "./TargetSelect";
import { UploadProgressItem, UploadProgressPopover } from "./UploadProgressPopover";

const folderKindsForSelect: FolderKind[] = ["folder"];

interface Props {
  dealId?: number;
  folderId?: number;
  files?: File[];
  isGlobalWorkspace?: boolean;
  onSuccessCallback?: () => Promise<void>;
  openDataExtractor?: boolean;
  acceptFileTypes?: string;
  isCloudMirror?: boolean;
}

export type FileValues = {
  file: File;
  document_type: string | null;
  investor_id: number | null;
  document_type_ai_suggested?: boolean;
  // Cloud mirror: a file can belong to several deals. The first id is treated as the primary deal
  // (used for the investor list). Empty/undefined in the non-cloud-mirror flow.
  deal_ids?: number[];
  effective_date?: string | null;
};

export type UploadStatus = "default" | "uploading" | "scanning" | "success" | "error";

export enum UploadFilesMode {
  Review = "review",
  Commit = "commit"
}

export const FilesUpload: React.FC<Props> = ({
  dealId,
  folderId: initialFolderId,
  files: initialFiles,
  onSuccessCallback,
  openDataExtractor,
  acceptFileTypes,
  isGlobalWorkspace,
  isCloudMirror
}) => {
  const openPrompt = usePrompt();
  const { closeFilesUploadModal } = useModals();

  const {
    getFoldersAction,
    getDealInvestors,
    getFilesDocumentTypesSuggestionsAction,
    getGlobalWorkspaceFoldersAction,
    uploadFileAction,
    uploadCloudMirrorSectionFilesAction,
    checkFilesVerifiedAction,
    getDealsTableRowsAction
  } = useDealActions();
  const fileScanningEnabled = useFileScanningSelector();
  const { show_recommendations } = useAccountSelector() || {};

  const { data: deals = [], action: fetchDeals } = useActionHook(() => getDealsTableRowsAction());

  useEffect(() => {
    fetchDeals();
  }, []);

  const lockedDeal = useMemo<DealBase | null>(
    () => (isCloudMirror && dealId ? deals.find((d) => d.id === dealId) || null : null),
    [isCloudMirror, dealId, deals]
  );

  const [step, setStep] = useState<number>(initialFiles?.length ? 2 : 1);
  const [deal, setDealState] = useState<DealBase | null>(
    isGlobalWorkspace ? GLOBAL_WORKSPACE_DEAL : deals.find((d) => d.id === dealId) || null
  );
  const [folderId, setFolderId] = useState<number | null>(initialFolderId || null);
  const [uploadFilesMode, setUploadFilesMode] = useState<UploadFilesMode>(UploadFilesMode.Review);

  const isGlobalWorkspaceSelected = deal?.id === GLOBAL_WORKSPACE_DEAL.id;

  const setDeal = useCallback((newDeal: DealBase | null) => {
    setDealState(newDeal);
    setFolderId(null);
  }, []);

  // Update deal when dealId prop changes or deals list is loaded
  useEffect(() => {
    if (isGlobalWorkspace) {
      setDealState(GLOBAL_WORKSPACE_DEAL);
      return;
    }

    if (dealId && deals.length) {
      const foundDeal = deals.find((d) => d.id === dealId);
      if (foundDeal && (!deal || deal.id !== dealId)) {
        setDealState(foundDeal);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, deals, isGlobalWorkspace]);

  const [filesValues, setFilesValues] = useState<FileValues[]>(() => {
    if (!initialFiles?.length) return [];
    const preselectedDeal = isCloudMirror && dealId ? deals.find((d) => d.id === dealId) : undefined;
    return initialFiles.map((file) => ({
      file,
      document_type: null,
      investor_id: null,
      ...(preselectedDeal && { deal_ids: [preselectedDeal.id] })
    }));
  });
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("default");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [selectFilesLoading, setSelectFilesLoading] = useState(false);
  const pollFilesUntilVerified = useCallback(
    async (scanningFileIds: number[], signal: AbortSignal): Promise<boolean> => {
      const MAX_RETRIES = 20;
      const RETRY_DELAY = 3000;
      const MAX_TOTAL_TIME = 60000;
      const startTime = Date.now();

      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        if (signal.aborted) return false;

        const elapsed = Date.now() - startTime;
        if (elapsed >= MAX_TOTAL_TIME) return false;

        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        if (signal.aborted) return false;

        try {
          await checkFilesVerifiedAction(scanningFileIds);
          return true; // 200 OK — all verified
        } catch {
          // 412 — not all verified yet, continue polling
        }
      }

      return false;
    },
    [checkFilesVerifiedAction]
  );

  const { action: getFolders, setData, loading: foldersLoading, data: folders = [] } = useActionHook(getFoldersAction);

  const {
    data: dealInvestors = [],
    loading: dealInvestorsLoading,
    action: getDealInvestorsAction
  } = useActionHook(getDealInvestors, {
    resInterceptor: (response) => (isArray(response) ? response.map((item) => ({ id: item.id, name: item.name })) : [])
  });

  useEffect(() => {
    if (deal && !isGlobalWorkspaceSelected) {
      getDealInvestorsAction({ dealId: deal.id });
    }
  }, [deal]);

  // Cloud mirror: per-deal investor cache
  const [dealInvestorsMap, setDealInvestorsMap] = useState<Record<number, { id: number; name: string }[]>>({});

  const fetchInvestorsForDeal = useCallback(
    async (targetDealId: number) => {
      if (dealInvestorsMap[targetDealId]) return;
      try {
        const response = await getDealInvestors({ dealId: targetDealId });
        const investors = isArray(response)
          ? response.map((item: { id: number; name: string }) => ({ id: item.id, name: item.name }))
          : [];
        setDealInvestorsMap((prev) => ({ ...prev, [targetDealId]: investors }));
      } catch {
        setDealInvestorsMap((prev) => ({ ...prev, [targetDealId]: [] }));
      }
    },
    [dealInvestorsMap, getDealInvestors]
  );

  useEffect(() => {
    if (!lockedDeal) return;
    fetchInvestorsForDeal(lockedDeal.id);
    setFilesValues((prev) => {
      const needsUpdate = prev.some((item) => item.deal_ids?.length !== 1 || item.deal_ids[0] !== lockedDeal.id);
      if (!needsUpdate) return prev;
      return prev.map((item) => ({ ...item, deal_ids: [lockedDeal.id] }));
    });
  }, [lockedDeal, fetchInvestorsForDeal]);

  const handleCloudMirrorDealChange = useCallback(
    (index: number, selectedDeals: DealBase[]) => {
      const ids = selectedDeals.map((d) => d.id);
      setFilesValues((prev) =>
        prev.map((item, i) => (i === index ? { ...item, deal_ids: ids, investor_id: null } : item))
      );
      // Investors come from the primary (first) deal.
      if (ids.length) {
        fetchInvestorsForDeal(ids[0]);
      }
    },
    [fetchInvestorsForDeal]
  );

  const handleBulkDealChange = useCallback(
    (newDeals: DealBase[]) => {
      const ids = newDeals.map((d) => d.id);
      setFilesValues((prev) =>
        prev.map((item, i) => (selected.has(i) ? { ...item, deal_ids: ids, investor_id: null } : item))
      );
      // Investors come from the primary (first) deal.
      if (ids.length) {
        fetchInvestorsForDeal(ids[0]);
      }
    },
    [selected, fetchInvestorsForDeal]
  );

  const isSubmitDisabled = useMemo(() => {
    if (step === 1) {
      if (isCloudMirror) return !filesValues.length;
      return !filesValues.length || (!isGlobalWorkspaceSelected && !deal);
    }

    if (step === 2) {
      if (isCloudMirror) {
        return filesValues.some((item) => !item.deal_ids?.length || !item.document_type);
      }
      return false;
    }

    return false;
  }, [step, filesValues, deal, isGlobalWorkspaceSelected, isCloudMirror]);

  const handleSelectFolderClick = useCallback(
    async (cb?: () => void) => {
      if (!deal || !folders) return;
      const selectedFolder = await openPrompt({
        Component: SelectDealFolder,
        props: {
          dealId: isGlobalWorkspaceSelected ? undefined : deal.id,
          title: "Select Folder",
          folders: isGlobalWorkspaceSelected
            ? folders
            : folders.filter((folder) => folderKindsForSelect.includes(folder.kind)),
          selectedFolderId: folderId,
          onFolderCreate: (newFolder: IFolder) => {
            setData((prev) => [...(prev || []), newFolder]);
          }
        }
      });
      if (selectedFolder) {
        setFolderId(selectedFolder.id);
        cb?.();
      }
    },
    [deal, folderId, folders, isGlobalWorkspaceSelected, openPrompt, setFolderId]
  );

  const onClickSelectFiles = useCallback(
    async (files?: FileList | File[]) => {
      setSelectFilesLoading(true);
      try {
        let fileArray = files
          ? Array.from(files)
          : Array.from((await pickFiles({ multiple: true, accept: acceptFileTypes })) || []);

        if (acceptFileTypes && files) {
          const extensions = acceptFileTypes.split(",").map((t) => t.trim().toLowerCase());
          fileArray = fileArray.filter((f) => extensions.some((ext) => f.name.toLowerCase().endsWith(ext)));
        }

        if (!fileArray.length) return;

        // const fileNames = fileArray.map((file) => file.name);
        // const documentTypes = await getFilesDocumentTypesSuggestionsAction(fileNames);
        // Mock document types in case Suggestion endpoint doesn't work
        const documentTypes = [] as Awaited<ReturnType<typeof getFilesDocumentTypesSuggestionsAction>>;

        setFilesValues((prev) => [
          ...prev,
          ...fileArray.map((file) => {
            const suggestion = documentTypes.find((item) => item.file_name === file.name);
            const documentType = suggestion?.document_type || null;
            return {
              file,
              document_type: documentType,
              investor_id: null,
              document_type_ai_suggested: !!documentType,
              ...(lockedDeal && { deal_ids: [lockedDeal.id] })
            };
          })
        ]);
      } finally {
        setSelectFilesLoading(false);
      }
    },
    [getFilesDocumentTypesSuggestionsAction, acceptFileTypes, lockedDeal]
  );

  const onClickClearFiles = () => setFilesValues([]);

  const updateDocumentType = useCallback((index: number, value: string | null) => {
    setFilesValues((prev) =>
      prev.map((item, i) => (i === index ? { ...item, document_type: value, document_type_ai_suggested: false } : item))
    );
  }, []);

  const updateDocumentTypeBulk = useCallback((indices: Set<number>, value: string | null) => {
    setFilesValues((prev) =>
      prev.map((item, i) =>
        indices.has(i) ? { ...item, document_type: value, document_type_ai_suggested: false } : item
      )
    );
  }, []);

  const updateEffectiveDate = useCallback((index: number, value: string | null) => {
    setFilesValues((prev) => prev.map((item, i) => (i === index ? { ...item, effective_date: value } : item)));
  }, []);

  const updateEffectiveDateBulk = useCallback((indices: Set<number>, value: string | null) => {
    setFilesValues((prev) => prev.map((item, i) => (indices.has(i) ? { ...item, effective_date: value } : item)));
  }, []);

  const stepsHandlers = [
    () => {
      if (isSubmitDisabled) return;
      if (isCloudMirror) {
        setStep(2);
        return;
      }
      if (!isGlobalWorkspaceSelected && !deal) return;
      if (!folderId) {
        handleSelectFolderClick(() => setStep(2));
        return;
      }
      setStep(2);
    },
    async () => {
      if (isSubmitDisabled) return;

      if (isCloudMirror) {
        setUploadStatus("uploading");
        setStep(3);

        const items = filesValues
          .filter((fv) => fv.deal_ids?.length && fv.document_type)
          .map((fv) => ({
            file: fv.file,
            deal_ids: fv.deal_ids!,
            document_type: fv.document_type!,
            ...(fv.investor_id != null && { investor_id: fv.investor_id }),
            ...(fv.effective_date && { effective_date: fv.effective_date }),
            mode: uploadFilesMode
          }));

        try {
          await uploadCloudMirrorSectionFilesAction(items);
          setUploadStatus("success");
          await onSuccessCallback?.();
        } catch (err) {
          const firstFileError = (err as { errors?: { file?: string[] } })?.errors?.file?.[0];
          if (firstFileError) toast.error(firstFileError);
          setUploadStatus("error");
        }
        return;
      }

      if (!isGlobalWorkspaceSelected && !deal) return;
      if (!folderId) {
        handleSelectFolderClick();
        return;
      }

      const downloads: Promise<IFile | IFile[]>[] = [];

      let resolveScan: (success: boolean) => void = () => {};
      const scanPromise = new Promise<boolean>((resolve) => {
        resolveScan = resolve;
      });

      const abortController = new AbortController();

      if (filesValues.length) {
        const uploadItems: UploadProgressItem[] = filesValues.map((fileValue) => ({
          file: fileValue.file,
          documentType: fileValue.document_type,
          promise: uploadFileAction({
            file: fileValue.file,
            ...(isGlobalWorkspaceSelected ? { isGlobalWorkspace: true } : { deal_id: deal!.id }),
            section_id: folderId,
            ...(fileValue.document_type && { document_type: fileValue.document_type }),
            ...(fileValue.investor_id && { investor_id: fileValue.investor_id }),
            ...(fileValue.effective_date && { effective_date: fileValue.effective_date }),
            mode: uploadFilesMode
          }),
          ...(fileScanningEnabled && { scanPromise })
        }));

        uploadItems.forEach((item) => downloads.push(item.promise));

        openPrompt({
          componentType: "status",
          statusTitle: "Upload Progress",
          Component: UploadProgressPopover,
          props: {
            isGlobalWorkspace: isGlobalWorkspaceSelected,
            items: uploadItems,
            onScanCancel: () => {
              abortController.abort();
              resolveScan(false);
            }
          }
        });
        closeFilesUploadModal();
      }
      const results = await Promise.allSettled(downloads);

      const rejectedResults = results.filter((result): result is PromiseRejectedResult => result.status === "rejected");
      const firstFileError = rejectedResults.map((result) => result.reason?.errors?.file?.[0]).find(Boolean);
      if (firstFileError) {
        toast.error(firstFileError);
      }

      const allUploadedFiles = results.flatMap((result) => {
        if (result.status !== "fulfilled" || !result.value) return [];
        return Array.isArray(result.value) ? result.value : [result.value];
      });

      if (!allUploadedFiles.length) {
        resolveScan(false);
        return;
      }

      if (abortController.signal.aborted) return;

      await onSuccessCallback?.();

      const scanningFiles = fileScanningEnabled ? allUploadedFiles.filter((f) => f.state === "scanning") : [];

      if (scanningFiles.length) {
        const scanSuccess = await pollFilesUntilVerified(
          scanningFiles.map((f) => f.id),
          abortController.signal
        );
        resolveScan(scanSuccess);
      } else {
        resolveScan(true);
      }
    }
  ];

  const handleSubmit = () => stepsHandlers[step - 1]?.();

  useEffect(() => {
    if (isGlobalWorkspaceSelected) {
      getGlobalWorkspaceFoldersAction().then((globalFolders) => setData(globalFolders));
      return;
    }
    if (!deal) {
      setData([]);
      return;
    }
    getFolders({ dealId: deal.id });
  }, [deal]);

  useEffect(() => {
    if (step === 1) {
      setSelected(new Set());
    }
  }, [step]);

  return (
    <Modal open={true} onClose={() => {}}>
      <>
        <Box sx={{ ...boxSx }}>
          <Stack gap="20px" sx={{ p: "32px" }}>
            <Head />
            <Stack gap="20px">
              {step < 3 && <StepIndicator step={step} isCloudMirror={isCloudMirror} />}
              <Stack gap="20px">
                {step === 1 && !isCloudMirror && (
                  <TargetSelect
                    deal={deal}
                    folderId={folderId}
                    // disableFolderSelect={Boolean(initialFolderId)}
                    folders={folders}
                    foldersLoading={foldersLoading}
                    setDeal={setDeal}
                    onSelectFolderClick={handleSelectFolderClick}
                    dealOptions={deals}
                  />
                )}
                {step === 1 && (
                  <FilesSelect
                    filesNumber={filesValues.length}
                    onClickSelectFiles={onClickSelectFiles}
                    onClickClearFiles={onClickClearFiles}
                    selectFilesLoading={selectFilesLoading}
                  />
                )}
                {step === 2 && (
                  <Stack>
                    {!isCloudMirror && (
                      <Stack direction="row" alignItems="center" gap="10px">
                        <Stack direction="row" alignItems="center" gap="5px">
                          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#496682" }}>Deal:</Typography>
                          <MaterialTooltip title={deal?.title}>
                            <Typography
                              noWrap
                              sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#496682",
                                display: "inline-block",
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {deal?.title}
                            </Typography>
                          </MaterialTooltip>
                        </Stack>
                        <FolderPathDisplay folders={folders} folderId={folderId} foldersLoading={foldersLoading} />
                      </Stack>
                    )}
                    <FilesTable
                      filesValues={filesValues}
                      setFilesValues={setFilesValues}
                      updateDocumentType={updateDocumentType}
                      updateEffectiveDate={updateEffectiveDate}
                      dealInvestors={dealInvestors}
                      dealInvestorsLoading={dealInvestorsLoading}
                      selected={selected}
                      setSelected={setSelected}
                      isCloudMirror={isCloudMirror}
                      dealInvestorsMap={dealInvestorsMap}
                      onDealChange={handleCloudMirrorDealChange}
                      lockedDealId={lockedDeal?.id ?? null}
                    />
                  </Stack>
                )}
                {step === 3 && isCloudMirror && (
                  <Result filesNumber={filesValues.length} uploadStatus={uploadStatus} isCloudMirror={isCloudMirror} />
                )}
              </Stack>
            </Stack>
            {step === 2 && show_recommendations && (
              <Stack direction="row" alignItems="center" gap="10px">
                <InfoOutlinedIcon sx={{ fontSize: 20, color: "#92A7BC" }} />
                <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#496682" }}>
                  Files classified as capital call notices, capital account statements, or distribution notices will
                  have values extracted automatically. Other files upload without extraction.
                </Typography>
              </Stack>
            )}
            <Footer
              step={step}
              isDisabled={isSubmitDisabled}
              onSubmit={handleSubmit}
              onBack={() => setStep(1)}
              dealId={deal?.id}
              folderId={folderId}
              hasDeal={!!deal}
              hasFiles={!!filesValues.length}
              hasAllDocumentTypes={filesValues.length > 0 && filesValues.every((item) => !!item.document_type)}
              openDataExtractor={openDataExtractor}
              isCloudMirror={isCloudMirror}
              setUploadFilesMode={setUploadFilesMode}
              uploadFilesMode={uploadFilesMode}
            />
          </Stack>
        </Box>
        {step === 2 && selected.size > 0 && (
          <BulkActions
            selected={selected}
            setFilesValues={setFilesValues}
            updateDocumentTypeBulk={updateDocumentTypeBulk}
            updateEffectiveDateBulk={updateEffectiveDateBulk}
            dealInvestors={dealInvestors}
            dealInvestorsLoading={dealInvestorsLoading}
            isCloudMirror={isCloudMirror}
            deals={deals}
            onBulkDealChange={handleBulkDealChange}
            lockedDealId={lockedDeal?.id ?? null}
          />
        )}
      </>
    </Modal>
  );
};

const boxSx: BoxProps["sx"] = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "white",
  borderRadius: "8px",
  boxShadow: "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
  width: "80%",
  overflow: "hidden"
};
